// Edit pane: confirmed records (GET /artifacts?status=confirmed, from the local
// store). Reopen to revise fields/tags, re-take a better picture, or re-run
// enrichment. Client-side search via Fuse.js over the loaded confirmed records.
// CONTRACTS §10.

import { useMemo, useState } from "react";
import Fuse from "fuse.js";
import type { Artifact } from "../types";
import { ArtifactCard } from "../components/ArtifactCard";

export interface EditPaneProps {
  items: Artifact[];
  loading: boolean;
  onChanged: () => void;
  onUpsert: (a: Artifact) => void;
  onRemove: (id: string) => void;
}

export function EditPane({ items, loading, onChanged, onUpsert, onRemove }: EditPaneProps) {
  const [query, setQuery] = useState("");

  const fuse = useMemo(
    () =>
      new Fuse(items, {
        includeScore: false,
        threshold: 0.4,
        ignoreLocation: true,
        keys: [
          { name: "title", weight: 3 },
          { name: "artist", weight: 3 },
          { name: "dateDisplay", weight: 1 },
          { name: "country", weight: 1 },
          { name: "medium", weight: 1 },
          { name: "styleTags", weight: 1 },
          { name: "generalTags", weight: 1 },
          { name: "tourTags", weight: 2 },
          { name: "captionText", weight: 1 },
          { name: "searchBlob", weight: 1 },
        ],
      }),
    [items],
  );

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return items;
    return fuse.search(q).map((r) => r.item);
  }, [query, fuse, items]);

  return (
    <div className="pane edit-pane">
      <div className="edit-toolbar">
        <input
          className="search-input"
          type="search"
          inputMode="search"
          placeholder="Search confirmed records (title, artist, tags, caption…)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="edit-toolbar__count">
          {query.trim() ? `${results.length} / ${items.length}` : `${items.length}`} confirmed
        </span>
      </div>

      {results.length === 0 ? (
        <p className="empty-state">
          {loading
            ? "Loading…"
            : items.length === 0
              ? "No confirmed records yet. Confirm items in the Review pane."
              : "No matches for that search."}
        </p>
      ) : (
        <div className="card-grid">
          {results.map((a) => (
            <ArtifactCard
              key={a.id}
              artifact={a}
              primaryLabel="Save changes"
              allowReenrich
              onSaved={onUpsert}
              onDeleted={onRemove}
              onReenriched={onChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}
